'use client';
import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';

import { Editor, EditorProps, OnMount, DiffEditor } from '@monaco-editor/react';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';

import { useLayoutShiftAwareness } from '../../hooks/useLayoutShiftAwareness';
import { Loader } from '../display/loader';

import type * as Monaco from 'monaco-editor';

type MonacoType = typeof Monaco;
type MonacoEditor = Monaco.editor.IStandaloneCodeEditor;

interface Props extends Omit<EditorProps, 'value' | 'onChange'> {
  disabled?: boolean;
  onBlur?: () => void;
  onChange: (value: string) => void;
  onMount?: (editor: MonacoEditor, monaco: MonacoType) => Promise<void> | void;
  pendingValue?: string;
  propSlots?: {
    editor: {
      colors?: Record<string, string>;
    };
  };
  value: string;
}

export const CodeEditor = forwardRef<MonacoEditor, Props>(
  (
    { disabled, onBlur, onChange, onMount, options = {}, pendingValue, propSlots, value, ...props },
    ref,
  ) => {
    const { palette } = useTheme();
    const [isLoading, setIsLoading] = useState(true);
    const [monacoInstance, setMonacoInstance] = useState<MonacoType | null>(null);
    const editorContainerRef = useRef<HTMLElement | null>(null);
    const editorRef = useRef<MonacoEditor | null>(null);

    const initMonaco = useCallback(async () => {
      try {
        const [monaco, { loader }] = await Promise.all([
          import('monaco-editor'),
          import('@monaco-editor/react'),
        ]);

        loader.config({ monaco });

        setMonacoInstance(monaco);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to initialize Monaco:', error);
      }
    }, []);

    useEffect(() => {
      initMonaco();

      // Suppress Monaco disposal errors, as we let the parent component handle the pending value which
      // toggles views
      const suppressMonacoError = (msg: string) =>
        msg?.includes('disposed before DiffEditorWidget model got reset');
      const handleError = (e: ErrorEvent) => suppressMonacoError(e.message) && e.preventDefault();
      const handleRejection = (e: PromiseRejectionEvent) =>
        suppressMonacoError(e.reason?.message || e.reason?.toString()) && e.preventDefault();
      window.addEventListener('error', handleError);
      window.addEventListener('unhandledrejection', handleRejection);

      return () => {
        window.removeEventListener('error', handleError);
        window.removeEventListener('unhandledrejection', handleRejection);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleEditorMount: OnMount = useCallback(
      async (editor, monaco) => {
        if (ref) {
          if (typeof ref === 'function') {
            ref(editor);
          } else {
            ref.current = editor;
          }
        }

        (window as any).__monacoEditor = editor;

        if (onBlur) {
          editor.onDidBlurEditorWidget(onBlur);
        }

        if (monacoInstance?.editor) {
          monacoInstance.editor.defineTheme('custom-theme', {
            base: 'vs-dark',
            colors: {
              'editor.background':
                propSlots?.editor?.colors?.['editor.background'] ?? palette.background.paper,
              'editor.foreground':
                propSlots?.editor?.colors?.['editor.foreground'] ?? palette.text.primary,
            },
            inherit: true,
            rules: [],
          });
          monacoInstance.editor.setTheme('custom-theme');
        }

        // Store editor and container refs
        editorRef.current = editor;
        editorContainerRef.current = editor.getContainerDomNode().parentElement;

        const handleResize = () => {
          const container = editor.getContainerDomNode().parentElement;
          if (container) {
            const rect = container.getBoundingClientRect();
            if (rect.width > 0 && rect.height > 0) {
              editor.layout({ height: rect.height, width: rect.width });
            }
          }
        };

        window.requestAnimationFrame(() => {
          handleResize();
        });

        window.addEventListener('resize', handleResize);

        (editor as any).__resizeCleanup = () => {
          window.removeEventListener('resize', handleResize);
        };

        if (onMount) {
          const onMountPromise = onMount(editor, monaco);
          if (onMountPromise instanceof Promise) {
            await new Promise((resolve) => {
              onMountPromise.catch(() => null).finally(() => resolve(null));
              setTimeout(resolve, 2000);
            });
          }
        }

        setIsLoading(false);
      },
      [
        monacoInstance,
        onMount,
        palette.background.paper,
        palette.text.primary,
        ref,
        onBlur,
        propSlots,
      ],
    );

    useEffect(() => {
      return () => {
        const editor = (window as any).__monacoEditor;
        if (editor && editor.__resizeCleanup) {
          editor.__resizeCleanup();
        }
      };
    }, []);

    // Use layout shift awareness hook to detect sidebar collapse/expand and resize the editor
    useLayoutShiftAwareness(() => {
      const editor = editorRef.current;
      const container = editorContainerRef.current;
      if (editor && container) {
        const rect = container.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) {
          editor.layout({ height: rect.height, width: rect.width });
        }
      }
    });

    if (!monacoInstance) {
      return <Loader />;
    }

    return (
      <Stack
        height="100%"
        minHeight={0}
        overflow="hidden"
        sx={{
          position: 'relative',
          visibility: isLoading ? 'hidden' : 'visible',
        }}
      >
        {isLoading && (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{
              bottom: 0,
              left: 0,
              position: 'absolute',
              right: 0,
              top: 0,
            }}
          >
            <Loader />
          </Stack>
        )}
        {pendingValue ? (
          <DiffEditor
            {...props}
            height="100%"
            language={props.language || props.defaultLanguage}
            loading={<Loader />}
            modified={pendingValue}
            onMount={(diffEditor) => {
              // Apply custom theme matching the regular editor
              if (monacoInstance?.editor) {
                monacoInstance.editor.defineTheme('custom-theme', {
                  base: 'vs-dark',
                  colors: {
                    'editor.background':
                      propSlots?.editor?.colors?.['editor.background'] ?? palette.background.paper,
                    'editor.foreground':
                      propSlots?.editor?.colors?.['editor.foreground'] ?? palette.text.primary,
                  },
                  inherit: true,
                  rules: [],
                });
                monacoInstance.editor.setTheme('custom-theme');
              }

              editorRef.current = diffEditor as any;
              editorContainerRef.current = diffEditor.getContainerDomNode().parentElement;

              // Simple resize handling
              const handleResize = () => {
                const container = diffEditor.getContainerDomNode().parentElement;
                if (container) {
                  const rect = container.getBoundingClientRect();
                  if (rect.width > 0 && rect.height > 0) {
                    diffEditor.layout({ height: rect.height, width: rect.width });
                  }
                }
              };

              window.requestAnimationFrame(handleResize);
              window.addEventListener('resize', handleResize);

              (diffEditor as any).__resizeCleanup = () => {
                window.removeEventListener('resize', handleResize);
              };
            }}
            options={{
              automaticLayout: false,
              minimap: {
                enabled: false,
              },
              readOnly: true,
              scrollbar: {
                alwaysConsumeMouseWheel: true,
              },
            }}
            original={value}
          />
        ) : (
          <Editor
            {...props}
            loading={<Loader />}
            onChange={(value) => onChange(value ?? '')}
            onMount={handleEditorMount}
            options={{
              ...options,
              automaticLayout: false, // ENG-1746: Scrollbar thrashing at breakpoints. Implemented resize handling above instead.
              readOnly: disabled,
              scrollbar: {
                ...options.scrollbar,
                alwaysConsumeMouseWheel: true,
              },
            }}
            value={value}
          />
        )}
      </Stack>
    );
  },
);
